import { useRef, useState } from "react";
import { ImagePlus, Loader2, X } from "lucide-react";
import { toast } from "sonner";

const MAX_DIM = 1600;

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function compress(file, keepPng) {
  const src = await readAsDataUrl(file);
  if (file.type === "image/svg+xml") return src;
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(img.width * scale);
  canvas.height = Math.round(img.height * scale);
  canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
  const png = keepPng && file.type === "image/png";
  return canvas.toDataURL(png ? "image/png" : "image/jpeg", 0.85);
}

export const ImageField = ({ value, onChange, keepPng, testId }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 12 * 1024 * 1024) { toast.error("Immagine troppo grande (max 12MB)"); return; }
    setBusy(true);
    try { onChange(await compress(file, keepPng)); } catch { toast.error("Impossibile leggere l'immagine"); }
    finally { setBusy(false); e.target.value = ""; }
  };

  return (
    <div className="mt-2 flex items-start gap-3">
      <div className="relative grid h-20 w-28 shrink-0 place-items-center overflow-hidden border border-white/15 bg-ink">
        {value ? <img src={value} alt="" className="h-full w-full object-contain" /> : <ImagePlus className="h-6 w-6 text-zinc-500" />}
        {value && (
          <button type="button" onClick={() => onChange("")} className="absolute right-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-ink/80" data-testid={`${testId}-clear`}>
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
      <div className="flex-1 space-y-2">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testId}-upload`}
          className="flex items-center gap-2 border border-white/15 px-3 py-2 font-mono text-xs uppercase tracking-widest text-zinc-200 hover:border-volt">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />} Carica file
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={onFile} />
        <input value={value?.startsWith("data:") ? "" : value || ""} onChange={(e) => onChange(e.target.value)} placeholder="…oppure incolla un URL"
          className="w-full border border-white/15 bg-ink px-3 py-2 text-sm text-white placeholder:text-zinc-500 focus:border-volt focus:outline-none" data-testid={`${testId}-url`} />
      </div>
    </div>
  );
};
